import React, { useState, useEffect, useRef } from 'react';
import { useStockMonitorStore } from '../../stores/useStockMonitorStore';
import { stockMonitorService } from '../../services/stockMonitorService';
import { SystemPromptInfo } from '../../services/adminService';
import MultiSelectDropdown from '../MultiSelectDropdown';
import SelectDropdown from '../SelectDropdown';
import { useToastStore } from '../../stores/useToastStore';

interface StockResearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (config: {
    minAmount?: number;
    monitorCount: number;
    strategyIds: string[];
    excludedSymbols: string[];
    systemPromptId?: string;
    filterAnalyzed: boolean;
    symbolKeyword?: string;
  }) => Promise<void>;
  loading: boolean;
  systemPrompts: SystemPromptInfo[];
  loadingPrompts: boolean;
}

const StockResearchDialog: React.FC<StockResearchDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading,
  systemPrompts,
  loadingPrompts,
}) => {
  const [monitorCount, setMonitorCount] = useState(5);
  const [strategyIds, setStrategyIds] = useState<string[]>(['hot_stocks']);
  const [strategies, setStrategies] = useState<any[]>([]);
  const [loadingStrategies, setLoadingStrategies] = useState(false);
  const [minAmount, setMinAmount] = useState(5000); // 最低成交额 (万)
  const [excludedSymbols, setExcludedSymbols] = useState<string[]>([]);
  const [selectedSystemPromptId, setSelectedSystemPromptId] = useState<string | undefined>(undefined);
  const [filterAnalyzed, setFilterAnalyzed] = useState(true);
  const [selectedSymbols, setSelectedSymbols] = useState<string[]>([]);
  const [symbolKeyword, setSymbolKeyword] = useState(''); // 保持兼容性，用于后端搜索

  const [availableSymbols, setAvailableSymbols] = useState<string[]>([]);
  const [symbolMap, setSymbolMap] = useState<Record<string, string>>({});
  const [loadingSymbols, setLoadingSymbols] = useState(false);
  // 预览状态
  const [previewSymbols, setPreviewSymbols] = useState<
    Array<{ symbol: string; name: string; amount: number; turnoverRate: number }>
  >([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const { getResearchConfig, isSyncingResearch } = useStockMonitorStore();

  const configLoadedRef = useRef(false);
  const loadingSymbolsRef = useRef(false);
  const searchDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const showToast = useToastStore((state) => state.show);

  const handleConfirm = async () => {
    // 提取纯代码用于提交
    const cleanExcludedSymbols = excludedSymbols.map((s) => {
      const match = s.match(/\((.*?)\)$/);
      return match ? match[1] : s;
    });

    const cleanSelectedSymbols = selectedSymbols.map((s) => {
      const match = s.match(/\((.*?)\)$/);
      return match ? match[1] : s;
    });

    const keywordParts = symbolKeyword.split(/[\s,]+/).filter(Boolean);
    const finalKeyword = Array.from(new Set([...cleanSelectedSymbols, ...keywordParts])).join(' ');

    try {
      await onConfirm({
        monitorCount,
        excludedSymbols: cleanExcludedSymbols,
        strategyIds,
        minAmount,
        filterAnalyzed,
        symbolKeyword: finalKeyword,
        systemPromptId: selectedSystemPromptId,
      });
      // onClose handled in parent
    } catch (error) {
      console.error('Confirm sync research tasks failed:', error);
    }
  };

  // 加载策略列表
  useEffect(() => {
    const loadStrategies = async () => {
      setLoadingStrategies(true);
      try {
        const data = await stockMonitorService.getAllStrategies();
        setStrategies(data);
      } catch (error) {
        console.error('加载策略失败:', error);
      } finally {
        setLoadingStrategies(false);
      }
    };
    if (isOpen) {
      loadStrategies();
    }
  }, [isOpen]);

  // 加载股票列表
  useEffect(() => {
    const loadSymbols = async () => {
      if (availableSymbols.length > 0 || loadingSymbolsRef.current) return;

      try {
        loadingSymbolsRef.current = true;
        setLoadingSymbols(true);

        // 获取所有A股列表 (limit=5000 以覆盖大多数股票)
        const stocks = await stockMonitorService.searchStocks('', 5000);

        const map: Record<string, string> = {};
        const formattedSymbols: string[] = [];

        stocks.forEach((s) => {
          map[s.symbol] = s.name;
          formattedSymbols.push(`${s.name} (${s.symbol})`);
        });

        setSymbolMap(map);
        setAvailableSymbols(formattedSymbols.sort());
      } catch (error: any) {
        console.error('获取股票列表失败:', error);
        showToast(error.message || '获取股票列表失败', 'error');
      } finally {
        setLoadingSymbols(false);
        loadingSymbolsRef.current = false;
      }
    };

    if (isOpen) {
      loadSymbols();
    }
  }, [isOpen, availableSymbols.length, showToast]);

  // 当股票列表加载完成后，格式化已有的排除列表
  useEffect(() => {
    if (Object.keys(symbolMap).length === 0) return;

    setExcludedSymbols((prev) =>
      prev.map((s) => {
        // 如果已经是格式化后的 (包含括号)，保持不变
        if (s.includes('(') && s.endsWith(')')) return s;
        // 如果是原始代码且有对应名称，进行格式化
        if (symbolMap[s]) return `${symbolMap[s]} (${s})`;
        return s;
      }),
    );
  }, [symbolMap]);

  // 加载初始配置
  useEffect(() => {
    if (!isOpen) {
      configLoadedRef.current = false;
      return;
    }

    if (configLoadedRef.current) return;
    configLoadedRef.current = true;

    const loadConfig = async () => {
      const config = await getResearchConfig();
      if (config) {
        setMonitorCount(config.monitorCount);
        setExcludedSymbols(config.excludedSymbols || []);
        if (config.strategyIds && Array.isArray(config.strategyIds)) {
          setStrategyIds(config.strategyIds);
        }
        if (config.minAmount) {
          setMinAmount(config.minAmount);
        }
        if (config.filterAnalyzed !== undefined) {
          setFilterAnalyzed(config.filterAnalyzed);
        }
        if (config.symbolKeyword) {
          setSymbolKeyword(config.symbolKeyword);
        }
      }
    };
    loadConfig();
  }, [isOpen, getResearchConfig]);

  // 加载预览股票
  useEffect(() => {
    if (!isOpen) return;

    const loadPreview = async () => {
      setLoadingPreview(true);
      try {
        // 合并手动选择的股票和输入的关键词
        const cleanSelectedSymbols = selectedSymbols.map((s) => {
          const match = s.match(/\((.*?)\)$/);
          return match ? match[1] : s;
        });
        const keywordParts = symbolKeyword.split(/[\s,]+/).filter(Boolean);
        const finalKeyword = Array.from(new Set([...cleanSelectedSymbols, ...keywordParts])).join(' ');

        const symbols = await stockMonitorService.previewResearchSymbols(
          monitorCount,
          excludedSymbols,
          strategyIds,
          minAmount,
          filterAnalyzed,
          finalKeyword,
        );
        setPreviewSymbols(symbols);
      } catch (error) {
        console.error('加载预览股票失败:', error);
      } finally {
        setLoadingPreview(false);
      }
    };

    const timer = setTimeout(loadPreview, 500);
    return () => clearTimeout(timer);
  }, [isOpen, monitorCount, excludedSymbols, strategyIds, minAmount, filterAnalyzed, symbolKeyword, selectedSymbols]);

  const handleKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSymbolKeyword(val);
  };

  const handleSearchDropdown = (keyword: string) => {
    if (!keyword.trim()) return;

    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(async () => {
      try {
        const stocks = await stockMonitorService.searchStocks(keyword, 20);
        if (stocks.length > 0) {
          setSymbolMap((prev) => {
            const newMap = { ...prev };
            stocks.forEach((s) => {
              newMap[s.symbol] = s.name;
            });
            return newMap;
          });

          setAvailableSymbols((prev) => {
            const currentSet = new Set(prev);
            let hasNew = false;
            stocks.forEach((s) => {
              const formatted = `${s.name} (${s.symbol})`;
              if (!currentSet.has(formatted)) {
                currentSet.add(formatted);
                hasNew = true;
              }
            });
            return hasNew ? Array.from(currentSet).sort() : prev;
          });
        }
      } catch (error) {
        console.error('搜索下拉股票失败:', error);
      }
    }, 500);
  };

  const isDialogLoading = loading || isSyncingResearch;

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-gray-900/95 backdrop-blur-md rounded-2xl border border-white/20 p-6 max-w-md w-full mx-4 shadow-2xl relative overflow-visible"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-white mb-2">同步A股研报任务</h3>
        <div className="text-xs text-slate-500 mb-4 bg-slate-800/50 p-2 rounded border border-slate-700/50">
          流程：选择策略 &rarr; 筛选目标 &rarr; 深度调研(Search+AI) &rarr;
          生成研报。仅同步任务数据，由后端管道异步处理。
        </div>

        <div className="mb-4 grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <div className="bg-white/5 p-4 rounded-xl border border-white/10 mb-4">
              <MultiSelectDropdown
                label="选股策略 (多个策略取交集)"
                value={strategyIds}
                onChange={(val) => setStrategyIds(val as string[])}
                options={strategies.map((s) => s.strategyId)}
                getOptionLabel={(id) => strategies.find((s) => s.strategyId === id)?.name || id}
                loading={loadingStrategies}
                disabled={isDialogLoading}
                placeholder="选择选股策略..."
                tagColor="blue"
              />

              <div className="mt-2 pt-2 border-t border-white/5">
                <MultiSelectDropdown
                  label="直接指定股票 (代码或名称)"
                  value={selectedSymbols}
                  onChange={setSelectedSymbols}
                  options={availableSymbols}
                  loading={loadingSymbols}
                  disabled={isDialogLoading}
                  placeholder="搜索并选择具体股票..."
                  searchPlaceholder="输入代码或名称搜索..."
                  tagColor="green"
                  onSearch={handleSearchDropdown}
                />

                <div className="mt-2">
                  <label className="block text-white/40 text-[10px] mb-1 font-bold uppercase tracking-wider">
                    其他关键词 (模糊搜索)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={symbolKeyword}
                      onChange={handleKeywordChange}
                      disabled={isDialogLoading}
                      placeholder="例如: 茅台 华为 (空格或逗号分隔)"
                      className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white text-xs focus:outline-none focus:ring-1 focus:ring-green-500/50 placeholder:text-white/20 transition-all disabled:opacity-50"
                    />
                  </div>
                </div>
                <p className="text-[9px] text-white/20 mt-2 leading-relaxed">
                  提示: 多条件取交集。若同时选了策略和股票，仅包含满足策略的指定股票。
                </p>
              </div>
            </div>

            <div className="flex items-center px-1">
              <input
                id="filterAnalyzed"
                type="checkbox"
                checked={filterAnalyzed}
                onChange={(e) => setFilterAnalyzed(e.target.checked)}
                disabled={isDialogLoading}
                className="w-4 h-4 bg-white/10 border border-white/20 rounded text-green-500 focus:ring-green-500/50 transition-all disabled:opacity-50"
              />
              <label
                htmlFor="filterAnalyzed"
                className="ml-2 text-white/60 text-xs cursor-pointer hover:text-white/80 transition-colors"
              >
                过滤近期已分析标的 (避免重复调研)
              </label>
            </div>
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-2">分析数量</label>
            <input
              type="number"
              min="1"
              max="50"
              value={monitorCount}
              onChange={(e) => setMonitorCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
              disabled={isDialogLoading}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-2">最低成交额 (万)</label>
            <input
              type="number"
              min="0"
              step="1000"
              value={minAmount}
              onChange={(e) => setMinAmount(Math.max(0, parseInt(e.target.value) || 0))}
              disabled={isDialogLoading}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
            />
          </div>
        </div>

        <MultiSelectDropdown
          label="排除股票"
          value={excludedSymbols}
          onChange={setExcludedSymbols}
          options={availableSymbols}
          loading={loadingSymbols}
          disabled={isDialogLoading}
          placeholder="点击选择要排除的股票..."
          searchPlaceholder="搜索股票..."
          emptyText="暂无股票数据"
          tagColor="red"
          onSearch={handleSearchDropdown}
        />

        <div className="mt-2 mb-4">
          <SelectDropdown
            label="系统提示词 (可选)"
            value={selectedSystemPromptId}
            onChange={(val) => setSelectedSystemPromptId(val as string | undefined)}
            options={[
              { value: '', label: '默认分析提示词' },
              ...systemPrompts.map((prompt) => ({
                value: prompt.promptId || prompt.id.toString(),
                label: prompt.promptId || prompt.title,
              })),
            ]}
            disabled={loadingPrompts || isDialogLoading}
            loading={loadingPrompts}
            placeholder="默认分析提示词"
          />
        </div>

        <div className="mt-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="text-white/70 text-sm">策略筛选预览 ({previewSymbols.length})</label>
            {loadingPreview && <span className="text-white/50 text-xs">加载中...</span>}
          </div>
          <div className="bg-black/20 rounded-lg p-2 max-h-32 overflow-y-auto">
            {previewSymbols.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {previewSymbols.map((s) => (
                  <span
                    key={s.symbol}
                    className="px-2 py-1 bg-white/10 rounded text-xs text-white/80 border border-white/10"
                    title={`名称: ${s.name}\n成交额: ${(s.amount / 100000000).toFixed(2)}亿\n换手率: ${s.turnoverRate}%`}
                  >
                    {s.name} ({s.symbol})
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-white/30 text-xs text-center py-4">暂无符合条件的股票</div>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleConfirm}
            className="flex-1 px-4 py-2 bg-green-500/20 border border-green-500/50 text-green-300 rounded-lg hover:bg-green-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isDialogLoading}
          >
            {isDialogLoading ? '同步中...' : '确认同步任务'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-white/10 border border-white/20 text-white/70 rounded-lg hover:bg-white/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isDialogLoading}
          >
            取消
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockResearchDialog;
